import { useState, useEffect, useRef, useContext } from "react";
import {
  Box,
  Checkbox,
  FormLabel,
  FormControlLabel,
  FormControl,
  FormGroup,
  Modal,
  Button,
  Divider,
  TextField,
} from "@mui/material";
import { DateRange } from "react-date-range";
import { frCH } from "date-fns/locale";
import TuneIcon from "@mui/icons-material/Tune";
import { device } from "../breakpoints";
import { MarkerData, Metadata } from "Types/markers";
import styled, { useTheme, ThemeContext } from "styled-components";
import locales from "locales/locales";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file

export const Filters = ({
  markers,
  metadata,
  handleFilter,
  currentSearch,
}: {
  markers: MarkerData[];
  metadata: Metadata | {};
  handleFilter: (filteredMarkers: MarkerData[]) => void;
  currentSearch: string;
}) => {
  // Calendar
  // NB monthIndex — The month as a number between 0 and 11 (January to December).
  const today = new Date();
  const fixedMinDate = new Date(2024, 10, 1);
  const maxDate = new Date(2025, 1, 0);
  // const minDate = today < maxDate ? new Date() : fixedMinDate;
  const minDate = fixedMinDate;

  const selectionRange = {
    startDate: minDate,
    endDate: maxDate,
    key: "selection",
  };
  const [currentRange, setCurrentRange] = useState([
    {
      startDate: today < maxDate ? new Date() : fixedMinDate,
      endDate: maxDate,
      key: "selection",
    },
  ]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [startDate, setStartDate] = useState(selectionRange.startDate);
  const [endDate, setEndDate] = useState(selectionRange.endDate);

  const [filters, setFilters] = useState(new Map());
  const [options, setOptions] = useState(new Map());
  const [showFilters, setShowFilters] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const theme = useTheme();
  const themeContext = useContext(ThemeContext);

  useEffect(() => {
    const counts = new Map();
    // Count the number of restaurants for each filter
    markers.forEach((marker) => {
      marker.filters.forEach((filter) => {
        let { name, value } = filter;
        if (!counts.has(name)) counts.set(name, new Map());
        const valueCounts = counts.get(name);
        if (valueCounts.has(value)) {
          valueCounts.set(value, {
            ...valueCounts.get(value),
            count: valueCounts.get(value).count + 1,
          });
        } else {
          valueCounts.set(value, { active: false, count: 1, name: value });
        }
      });
    });

    setFilters(counts);
  }, [markers]);

  useEffect(() => {
    let filteredMarkers = markers;

    // Find only the relevant markers, according to the filters
    filteredMarkers = markers.filter((m) => {
      let keep = true;

      // Dates
      console.log(
        m.name,
        m.start_date,
        m.end_date,
        `Filter: ${startDate}-${endDate}`,
        new Date(m.start_date) <= endDate,
        new Date(m.end_date) >= startDate
      );
      keep =
        new Date(m.start_date) <= endDate && new Date(m.end_date) >= startDate;

      filters.forEach((value, key) => {
        if (keep) {
          const markerValues = m.filters
            .filter((f) => f.name === key)
            .map((f) => f.value);
          const multiValue = markerValues.find((value) =>
            value.toString().includes(",")
          );
          if (multiValue) {
            markerValues.push(...multiValue.toString().split(", "));
          }

          const filterValues = [...value.values()]
            .filter((v) => v.active)
            .map((v) => v.name);

          keep = markerValues.some((mv) => filterValues.includes(mv));

          // If no filter is active in this category, show the marker
          if (filterValues.length === 0) keep = true;
        }
      });
      return keep;
    });

    // Find only the relevant markers, according to the search box
    filteredMarkers = filteredMarkers.filter((m) => {
      if (currentSearch !== "") {
        return m.text
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .includes(
            currentSearch
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .toLowerCase()
          );
      } else {
        return true;
      }
    });

    handleFilter(filteredMarkers);
  }, [filters, currentSearch, startDate, endDate]);

  // Calendar callbacks
  const handleInputClick = () => {
    setShowCalendar(!showCalendar);
  };

  const handleSelect = (ranges: any) => {
    setCurrentRange([ranges.selection]);
    setStartDate(ranges.selection.startDate);
    setEndDate(ranges.selection.endDate);
    console.log(
      `New date range: ${ranges.selection.startDate}, ${ranges.selection.endDate}`
    );
    if (ranges.selection.startDate !== ranges.selection.endDate) {
      setShowCalendar(false);
    }
  };

  function compareFilters(a: any, b: any) {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  }

  let freeFilter = filters.get("Gratuit")
    ? [...filters.get("Gratuit").values()].sort(compareFilters)
    : [];
  if (freeFilter.length > 1) {
    freeFilter = freeFilter.filter((f) => f.name !== "");
  }

  return (
    <Wrapper>
      <Button
        ref={filterButtonRef}
        color="info"
        disableElevation
        startIcon={<TuneIcon />}
        variant="contained"
        onClick={() => {
          setShowFilters(!showFilters);
          if (filterButtonRef.current) {
            const rect = filterButtonRef.current.getBoundingClientRect();
            if (rect.top > 200) {
              filterButtonRef.current.scrollIntoView();
            }
          }
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "Interactions",
            event_action: "click",
            event_label: `showfilters`,
          });
        }}
      >
        {locales.buttonFilters[theme.lang as "fr" | "de"]}
      </Button>
      <Modal
        open={showFilters}
        onClose={() => {
          setShowFilters(false);
        }}
      >
        <Window>
          <MenuContainer>
            <MenuInfos>
              {locales.filtersTitle[theme.lang as "fr" | "de"]}
            </MenuInfos>
            <CloseButton
              onClick={() => {
                setShowFilters(false);
              }}
            >
              <Close />
            </CloseButton>
          </MenuContainer>

          <Content>
            <Box mb={2} pt={2}>
              <FormControl fullWidth>
                <FormLabel sx={{ color: "inherit" }} id="is-open">
                  Dates
                </FormLabel>

                {showCalendar ? (
                  <DateRangeWrapper>
                    <DateRange
                      locale={frCH}
                      shownDate={today < maxDate ? new Date() : minDate}
                      color={"#2c3890"}
                      dateDisplayFormat="d MMM yyyy"
                      editableDateInputs={true}
                      moveRangeOnFirstSelection={false}
                      ranges={currentRange}
                      onChange={handleSelect}
                    />
                  </DateRangeWrapper>
                ) : (
                  <FormGroup>
                    <TextField
                      className="date-range-display ui input text"
                      type="text"
                      value={
                        "Du " +
                        startDate.toLocaleDateString() +
                        " au " +
                        endDate.toLocaleDateString()
                      }
                      onClick={handleInputClick}
                    />
                  </FormGroup>
                )}
              </FormControl>
            </Box>

            <Divider />

            <Footer>
              <Button
                onClick={() => {
                  const newFilters = new Map(filters);
                  newFilters.forEach((v, k) => {
                    v.forEach((a: any) => {
                      a.active = false;
                    });
                  });
                  setFilters(newFilters);
                }}
              >
                {locales.buttonResetFilters[theme.lang as "fr" | "de"]}
              </Button>
              <Button
                color="info"
                disableElevation
                variant="contained"
                onClick={() => {
                  setShowFilters(false);
                }}
              >
                {locales.buttonApplyFilters[theme.lang as "fr" | "de"]}
              </Button>
            </Footer>
          </Content>
        </Window>
      </Modal>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  color: ${(props) => props.theme.textColor};
`;
const Window = styled.div`
  border-radius: 0;
  background-color: ${(props) => props.theme.plainColor};
  text-align: left;
  box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.2);
  max-width: calc(100% - 32px);
  height: 80vh;
  margin: auto;
  margin-top: 10vh;
  position: relative;
  font-family: var(--font-sans-serif);

  overscroll-behavior-y: none;
  scroll-behavior: smooth;

  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    width: 100%;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    width: 640px;
  }
`;

const CloseButton = styled.div`
  background-color: var(--text-color);
  margin-left: auto;
  height: 100%;
  width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 10vh;
  position: absolute;
  right: 0;
  top: 0;

  &:after {
    content: "";
    margin-top: 75%;
  }
`;

const Close = styled.div`
  position: relative;
  display: inline-block;
  width: 32px;
  height: 32px;
  opacity: 0.75;

  &:before,
  &:after {
    left: 15px;
    position: absolute;
    content: " ";
    height: 33px;
    width: 2px;
    background-color: ${(props) => props.theme.plainColor};
  }
  &:before {
    transform: rotate(45deg);
  }
  &:after {
    transform: rotate(-45deg);
  }
`;

const MenuContainer = styled.div`
  height: 50px;
  font-weight: 100;
  user-select: none;
  background-color: ${(props) => props.theme.plainColor};
  border-bottom: 1px solid ${(props) => props.theme.borderColor};
  position: sticky;
  transform: translate3d(0, 0, 0);
  top: 0;
  z-index: 2;
  display: flex;
  justify-content: space-around;
  align-items: stretch;
`;

const MenuInfos = styled.div`
  color: ${(props) => props.theme.textColor};
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  line-height: 1.5;
  padding-right: 2rem;
  width: calc(100% - 20vh -1em);
  float: left;
  height: 100%;
  cursor: pointer;
  font-weight: bold;
  font-size: 1.5rem;
  max-width: 600px;
  padding: 0 2rem;
  margin: auto;
  width: 100%;
`;

const Footer = styled.div`
  padding: 35px 0;
  text-align: right;
`;

const Content = styled.div`
  padding: 35px;
  padding-top: 0;
  color: ${(props) => props.theme.textColor};
`;
const DateRangeWrapper = styled.div`
  // Default theme: https://github.com/hypeserver/react-date-range/blob/master/src/theme/default.scss
  // Dark mode: quick and dirty for now

  .rdrCalendarWrapper {
    color: ${(props) => props.theme.textColor};
    background-color: ${(props) => props.theme.plainColor};
    font-family: var(--font-sans-serif);
  }
  .rdrMonthAndYearPickers select {
    font-family: var(--font-sans-serif);
  }

  .rdrDateDisplayWrapper,
  .rdrDateInput {
    color: ${(props) => props.theme.textColor};
    background-color: ${(props) => props.theme.greyColor};
    input {
      color: ${(props) => props.theme.textColor};
      font-family: var(--font-sans-serif);
    }
  }
  .rdrNextPrevButton {
    color: ${(props) => props.theme.textColor};
    background-color: ${(props) => props.theme.greyColor};
  }
  .rdrSelected,
  .rdrInRange,
  .rdrStartEdge,
  .rdrEndEdge {
    background-color: ${(props) => props.theme.mainColor};
  }
  .rdrDayNumber span {
    color: ${(props) => props.theme.textColor};
  }
  .rdrDayPassive {
    pointer-events: none;
    .rdrDayNumber span {
      color: ${(props) => props.theme.borderColor};
    }
  }

  .rdrDayDisabled {
    background-color: rgb(248, 248, 248);
    .rdrDayNumber span {
      color: ${(props) => props.theme.greyColor};
    }
    .rdrInRange,
    .rdrStartEdge,
    .rdrEndEdge,
    .rdrSelected,
    .rdrDayStartPreview,
    .rdrDayInPreview,
    .rdrDayEndPreview {
      filter: grayscale(100%) opacity(60%);
      color: pink;
    }
  }
  // .rdrDateDisplayItem {
  //   background-color: blue;
  // }
  // .rdrDateDisplayItemActive {
  //   background-color: ${(props) => props.theme.mainColor};
  // }
  // .rdrDayStartPreview,
  // .rdrDayInPreview,
  // .rdrDayEndPreview {
  //   background-color: pink;
  // }
  // rdrDefinedRangesWrapper {
  //   background-color: grey;
  // }
`;
